import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Sparkles, Award, Smartphone, BookOpen, Activity, Target, CheckCircle2, Star, TrendingUp, Calendar, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { services, features, testimonials, stats, faqs, steps } from "../mock";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";


const iconMap = { ShieldCheck, Sparkles, Award, Smartphone, BookOpen, Activity, Target };

// Rotating word — cycles through action verbs with a vertical slide animation
const RotatingWord = ({ words, interval = 1800 }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % words.length), interval);
    return () => clearInterval(t);
  }, [words, interval]);
  return (
    <span className="relative inline-block h-[1.1em] overflow-hidden align-baseline" style={{ minWidth: "5.5ch" }}>
      {words.map((w, i) => (
        <span
          key={i}
          className={`block transition-all duration-500 absolute inset-0 text-knight-gold ${i === idx ? "opacity-100 translate-y-0" : i < idx ? "opacity-0 -translate-y-full" : "opacity-0 translate-y-full"}`}
        >
          {w}
        </span>
      ))}
      {/* invisible spacer for width */}
      <span className="invisible">{words.reduce((a, b) => a.length > b.length ? a : b, "")}</span>
    </span>
  );
};

// Deletion-stack animation — credit-report rows progressively flip to "DELETED" then reset
const DeletionStack = () => {
  const items = useMemo(() => ([
    { label: "Collection", amount: "$1,240", bureau: "TransUnion" },
    { label: "Late Payment", amount: "$320", bureau: "Equifax" },
    { label: "Charge-Off", amount: "$2,890", bureau: "Experian" },
    { label: "Hard Inquiry", amount: "\u2014", bureau: "All 3 Bureaus" },
    { label: "Public Record", amount: "$4,150", bureau: "TransUnion" },
  ]), []);

  const [step, setStep] = useState(0); // 0..items.length
  const [score, setScore] = useState(540);

  useEffect(() => {
    const interval = setInterval(() => {
      setStep(s => {
        const next = s + 1;
        if (next > items.length + 1) return 0;
        return next;
      });
    }, 1400);
    return () => clearInterval(interval);
  }, [items.length]);

  useEffect(() => {
    // Score climbs as items are deleted
    const target = 540 + Math.min(items.length, step) * 48;
    let raf;
    const t0 = performance.now();
    const start = score;
    const dur = 900;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setScore(Math.round(start + (target - start) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const grade = score >= 750 ? "Excellent" : score >= 700 ? "Very Good" : score >= 670 ? "Good" : score >= 580 ? "Fair" : "Poor";
  const gain = score - 540;

  return (
    <div className="relative w-full max-w-md mx-auto bg-white rounded-3xl shadow-2xl p-6">
      {/* Header bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-[10px] font-bold text-knight-navy/60 tracking-widest">LIVE DISPUTE ACTIVITY</span>
        </div>
        <span className="text-[10px] font-mono text-knight-navy/40">{new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" })}</span>
      </div>

      {/* Score banner */}
      <div className="relative rounded-2xl overflow-hidden mb-4 p-5 knight-gradient text-white">
        <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-knight-gold/20 blur-2xl" />
        <div className="relative flex items-baseline justify-between">
          <div>
            <p className="text-[10px] tracking-widest text-white/60 font-bold mb-1">CREDIT SCORE</p>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-5xl font-extrabold tabular-nums">{score}</span>
              <span className="font-bold text-knight-gold text-sm">{grade}</span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] tracking-widest text-white/60 font-bold mb-1">GAIN</p>
            <span className="inline-flex items-center gap-1 font-bold text-emerald-300 text-lg">
              <TrendingUp size={16} /> +{gain}
            </span>
          </div>
        </div>
        {/* Progress bar */}
        <div className="mt-3 h-1.5 rounded-full bg-white/20 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-red-400 via-amber-400 to-emerald-500 rounded-full transition-[width] duration-700"
               style={{ width: `${Math.min(100, ((score - 300) / 550) * 100)}%` }} />
        </div>
      </div>

      {/* Report rows */}
      <div className="space-y-2">
        {items.map((it, i) => {
          const removed = i < step;
          return (
            <div
              key={i}
              className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all duration-500 ${removed ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}
              style={{ transform: removed ? "translateX(0) scale(1)" : "translateX(0)", opacity: 1 }}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-500 ${removed ? "bg-emerald-100 text-emerald-600" : "bg-red-100 text-red-500"}`}>
                  {removed ? <CheckCircle2 size={16} /> : <XIcon size={16} />}
                </div>
                <div className="min-w-0">
                  <p className={`text-sm font-bold transition-all duration-500 ${removed ? "text-emerald-700 line-through decoration-2 decoration-emerald-400" : "text-knight-navy"}`}>{it.label}</p>
                  <p className="text-[10px] text-knight-navy/50">{it.bureau} \u00b7 {it.amount}</p>
                </div>
              </div>
              <span className={`text-[10px] font-bold tracking-widest px-2 py-1 rounded-md transition-colors duration-500 ${removed ? "bg-emerald-600 text-white" : "bg-slate-200 text-knight-navy/60"}`}>
                {removed ? "DELETED" : "ACTIVE"}
              </span>
            </div>
          );
        })}
      </div>

      {/* Bottom badge */}
      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] font-semibold text-knight-navy/60">
        <ShieldCheck size={13} className="text-knight-gold" />
        <span>FCRA Compliant Dispute Process</span>
      </div>
    </div>
  );
};

// Tiny X icon (avoid Lucide name clash with router)
const XIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// Hero section
export const Hero = () => {
  return (
    <section className="relative overflow-hidden knight-gradient text-white">
      <div className="absolute inset-0 grain-overlay" />
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-knight-gold/10 blur-3xl" />
      <div className="absolute -bottom-40 -left-32 w-[500px] h-[500px] rounded-full bg-blue-500/20 blur-3xl" />

      {/* Knight shield watermark — left aligned, positioned upward */}
      <img
        src="https://customer-assets.emergentagent.com/job_knight-dashboard-2/artifacts/dpdc5mg5_KCC-NoBG.png"
        alt=""
        aria-hidden="true"
        className="hidden md:block absolute left-[-40px] top-[8%] h-[95%] w-auto pointer-events-none select-none animate-float z-[5]"
        style={{ opacity: 0.28, filter: "drop-shadow(0 30px 60px rgba(244, 180, 0, 0.45))" }}
        draggable={false}
      />

      <div className="relative max-w-7xl mx-auto px-4 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up relative z-10 text-right flex flex-col items-end">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-4 backdrop-blur">
            <Sparkles size={14} className="text-knight-gold" />
            <span className="text-xs font-semibold tracking-wide">Welcome to Knight Credit Consultants</span>
          </div>
          <h1 className="font-display text-5xl lg:text-7xl font-bold leading-[1.05] mb-5 uppercase tracking-tight">
            Your <span className="text-knight-gold">Knight</span> in shining armor to good credit.
          </h1>

          {/* Rotating verb strip — original take on the cycling-word animation idea */}
          <div className="mb-6 flex items-baseline justify-end gap-2 text-xl lg:text-2xl font-display">
            <span className="text-white/70">We help you</span>
            <RotatingWord words={["Dispute.", "Remove.", "Repair.", "Rebuild.", "Rise."]} />
          </div>

          <p className="text-lg text-white/80 max-w-xl mb-8 leading-relaxed">
            Customized credit solutions to help you regain control of your financial future. Honest. Transparent. Results-driven.
          </p>
          <div className="flex flex-wrap gap-3 justify-end">
            <a href="https://zcal.co/knightmediagroup/kcc1" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-knight-gold hover:bg-knight-gold/90 text-knight-navy font-bold btn-lift">
                <Calendar size={18} className="mr-2" /> Schedule A Consultation
              </Button>
            </a>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-white/40 text-white hover:bg-white hover:text-knight-navy btn-lift">
                Explore Services <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        <div className="relative z-10">
          <DeletionStack />
        </div>
      </div>
    </section>
  );
};

// Animated stats counter — re-fires every time it enters the viewport
export const StatsBar = () => {
  const ref = useRef(null);
  const [runKey, setRunKey] = useState(0);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setRunKey(k => k + 1);
    }, { threshold: 0.3 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-white border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
        {stats.map((s, i) => (
          <Counter key={`${runKey}-${i}`} value={s.value} suffix={s.suffix} label={s.label} run={runKey > 0} />
        ))}
      </div>
    </section>
  );
};

const Counter = ({ value, suffix, label, run }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!run) return;
    let raf, start = performance.now(), dur = 1800;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [run, value]);
  return (
    <div className="text-center">
      <div className="font-display text-4xl lg:text-5xl font-bold text-knight-navy">
        {n}<span className="text-knight-gold">{suffix}</span>
      </div>
      <div className="text-sm font-medium text-knight-navy/60 mt-1">{label}</div>
    </div>
  );
};

// Why Choose Us
export const WhyUs = () => (
  <section className="py-20 subtle-gradient">
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center max-w-3xl mx-auto mb-14">
        <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">WHY CHOOSE US</p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-knight-navy mb-4 text-center md:whitespace-nowrap">Why Choose Knight Credit Consultants?</h2>
        <p className="text-knight-navy/70 leading-relaxed">Improving your credit is more than just numbers&mdash;it's about creating new opportunities for you and your family. Our experienced team is dedicated to helping you build a secure financial future with clear, customized strategies.</p>
      </div>

      {/* Reasons (left) + family image (right) */}
      <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
        <div className="grid sm:grid-cols-2 gap-5 order-2 lg:order-1">
          {features.map((f, i) => {
            const Icon = iconMap[f.icon];
            return (
              <Card key={i} className="card-intense group border-slate-200 hover:border-knight-gold overflow-hidden rounded-2xl">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-knight-navy text-knight-gold flex items-center justify-center mb-4 group-hover:bg-knight-gold group-hover:text-knight-navy transition-colors">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-display font-bold text-lg text-knight-navy mb-2 leading-snug">{f.title}</h3>
                  <p className="text-sm text-knight-navy/70 leading-relaxed">{f.text}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="relative order-1 lg:order-2">
          <div className="absolute -inset-4 bg-knight-gold/15 rounded-3xl rotate-3" />
          <img
            src="https://customer-assets.emergentagent.com/job_d3492e73-7acd-4186-bd61-8b4a9936405d/artifacts/jyppcu8d_recreate-add-a-home-sale-sign-in-plain-s_cS_LqyezUC2-ikyzX4B6BA_q9KTEdP0QXOngz8-GTysYQ.png"
            alt="A happy family in front of their newly purchased home with a SOLD sign"
            className="relative w-full h-[520px] object-cover rounded-3xl shadow-2xl"
          />
          <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 max-w-[240px] hidden sm:block">
            <p className="text-knight-gold text-[11px] font-bold tracking-widest mb-1">BUILT FOR FAMILIES</p>
            <p className="font-display text-base font-bold text-knight-navy leading-snug">Better credit, better life for the people you love.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Services preview
export const ServicesPreview = () => (
  <section className="relative py-24 knight-gradient text-white overflow-hidden">
    {/* Decorative grain + gold orb */}
    <div className="absolute inset-0 grain-overlay opacity-50 pointer-events-none" />
    <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-knight-gold/10 rounded-full blur-3xl pointer-events-none" />
    <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-knight-gold/5 rounded-full blur-3xl pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-4">
      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 mb-4 backdrop-blur">
          <Sparkles size={14} className="text-knight-gold" />
          <p className="text-knight-gold font-semibold tracking-widest text-xs">OUR SERVICES</p>
        </div>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white text-center whitespace-nowrap">Customized Credit Solutions Tailored for You</h2>
        <p className="text-white/65 max-w-2xl mx-auto mt-4 leading-relaxed">Four pillars working together to lift your score, clear your report, and keep it that way.</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {services.map((s, i) => {
          const Icon = iconMap[s.icon];
          return (
            <Link
              key={s.id}
              to={`/services#${s.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative rounded-3xl p-[1.5px] overflow-hidden bg-gradient-to-br from-white/10 via-knight-gold/30 to-white/5 hover:from-knight-gold hover:via-knight-gold hover:to-knight-gold transition-all duration-500"
            >
              <div className="relative h-full rounded-[22px] bg-knight-navy-dark/80 backdrop-blur p-7 flex flex-col transition-transform duration-500 group-hover:-translate-y-2">
                {/* Big watermark number */}
                <div className="absolute top-3 right-5 font-display text-7xl font-extrabold text-white/[0.04] group-hover:text-knight-gold/15 transition-colors select-none">
                  0{s.id}
                </div>

                {/* Icon block with glow halo */}
                <div className="relative inline-flex items-center justify-center mb-6">
                  <div className="absolute inset-0 rounded-2xl bg-knight-gold/30 blur-2xl scale-125 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="relative w-16 h-16 rounded-2xl bg-white/5 ring-1 ring-white/10 text-knight-gold flex items-center justify-center transition-all duration-500 group-hover:bg-knight-gold group-hover:text-knight-navy group-hover:-rotate-6 group-hover:scale-110 group-hover:ring-knight-gold">
                    <Icon size={28} />
                  </div>
                </div>

                <h3 className="font-display font-bold text-2xl mb-3 text-white">{s.title}</h3>
                <p className="text-sm text-white/65 leading-relaxed mb-5 flex-1">{s.short}</p>

                <span className="inline-flex items-center gap-1 text-sm font-bold text-knight-gold group-hover:translate-x-1 transition-transform duration-300">
                  Learn more <ChevronRight size={14} />
                </span>

                {/* Bottom growing accent */}
                <div className="absolute bottom-0 left-0 h-[3px] bg-gradient-to-r from-knight-gold to-knight-gold/40 w-0 group-hover:w-full transition-all duration-500" />
              </div>
            </Link>
          );
        })}
      </div>

      <div className="mt-12 text-center">
        <Link to="/services">
          <Button size="lg" className="bg-knight-gold hover:bg-amber-400 text-knight-navy font-bold btn-lift px-7">
            View All Services <ArrowRight size={16} className="ml-2" />
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

// Process Diagram — animated horizontal flow chart with connecting line + 4 step nodes
export const ProcessDiagram = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setVisible(true);
    }, { threshold: 0.3 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  const steps = [
    { icon: Calendar, title: "Free Consultation", text: "We review your three-bureau report and discuss your credit goals \u2014 no obligation." },
    { icon: Target, title: "Custom Strategy", text: "Your dispute roadmap is built around your exact accounts and target score." },
    { icon: ShieldCheck, title: "Dispute Rounds", text: "FCRA-compliant disputes mailed to all three bureaus, refreshed every 60 days." },
    { icon: TrendingUp, title: "Score Results", text: "Watch deletions roll in and your score climb through your secure portal." },
  ];

  return (
    <section ref={ref} className="py-20 subtle-gradient">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">THE PROCESS DIAGRAM</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-knight-navy mb-3 text-center">How we&apos;ll get you to a better score</h2>
          <p className="text-knight-navy/70 leading-relaxed">A clear, four-stage journey from your first call to long-term credit health.</p>
        </div>

        <div className="relative">
          {/* Animated SVG flow line (desktop horizontal, mobile vertical) */}
          <svg
            aria-hidden="true"
            className="hidden lg:block absolute top-[60px] left-[7%] right-[7%] w-[86%] h-2 pointer-events-none"
            viewBox="0 0 1000 8"
            preserveAspectRatio="none"
          >
            <line x1="0" y1="4" x2="1000" y2="4" stroke="#e2e8f0" strokeWidth="3" strokeDasharray="6 8" strokeLinecap="round" />
            <line
              x1="0" y1="4" x2="1000" y2="4"
              stroke="url(#flowGrad)" strokeWidth="3" strokeDasharray="6 8" strokeLinecap="round"
              style={{
                strokeDashoffset: visible ? 0 : 1000,
                strokeDasharray: 1000,
                transition: "stroke-dashoffset 2.2s ease-out",
              }}
            />
            <defs>
              <linearGradient id="flowGrad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stopColor="#0b1c5c" />
                <stop offset="0.6" stopColor="#1f3aa8" />
                <stop offset="1" stopColor="#f4b400" />
              </linearGradient>
            </defs>
          </svg>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-10 lg:gap-6 relative">
            {steps.map((s, i) => (
              <div
                key={i}
                className="relative flex flex-col items-center text-center transition-all duration-700"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(20px)",
                  transitionDelay: `${i * 300 + 400}ms`,
                }}
              >
                {/* Vertical connector for mobile */}
                {i < steps.length - 1 && (
                  <div className="lg:hidden absolute left-1/2 -translate-x-1/2 top-[120px] w-0.5 h-12 bg-gradient-to-b from-knight-navy/40 to-knight-gold/40" />
                )}

                {/* Step number ribbon */}
                <div className="relative mb-5">
                  <div className="w-32 h-32 rounded-full bg-white border-4 border-knight-gold shadow-xl flex flex-col items-center justify-center relative">
                    <div className="absolute -top-2 -right-2 w-9 h-9 rounded-full bg-knight-navy text-knight-gold font-bold flex items-center justify-center text-sm shadow-md">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <s.icon size={36} className="text-knight-navy" />
                    {/* Pulse ring while drawing */}
                    {visible && (
                      <span className="absolute inset-0 rounded-full border-4 border-knight-gold opacity-60 animate-ping" style={{ animationDelay: `${i * 300}ms`, animationDuration: "1.6s", animationIterationCount: 1 }} />
                    )}
                  </div>
                </div>

                <h3 className="font-display font-bold text-xl text-knight-navy mb-2">{s.title}</h3>
                <p className="text-sm text-knight-navy/70 leading-relaxed max-w-[240px]">{s.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Outcome chip below */}
        <div className="mt-14 flex justify-center">
          <div className="inline-flex items-center gap-3 bg-knight-navy text-white rounded-full px-6 py-3 shadow-xl">
            <Sparkles size={18} className="text-knight-gold" />
            <span className="font-semibold text-sm">Initial deletions typically appear within 30&ndash;60 days</span>
          </div>
        </div>

        {/* Results disclaimer */}
        <p className="mt-6 text-center text-xs text-knight-navy/55 max-w-2xl mx-auto" data-testid="process-results-disclaimer">
          *Results vary based on individual credit profiles and are not guaranteed.
        </p>
      </div>
    </section>
  );
};

// About the Process — 4-pillar trust grid placed between Services and the step-by-step timeline
export const AboutTheProcess = () => {
  const pillars = [
    { icon: Award, title: "Proven Track Record", text: "Hundreds of clients have improved their scores with our disciplined dispute methodology." },
    { icon: Sparkles, title: "Quick Turnaround", text: "Initial deletions and score movement typically appear within the first 30\u201360 days." },
    { icon: Target, title: "Personalized Strategy", text: "Every credit profile is unique. We design a plan around your exact accounts and goals." },
    { icon: ShieldCheck, title: "Round-the-Clock Support", text: "24/7 mobile portal access plus direct client lines whenever you need answers." },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">ABOUT THE PROCESS</p>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-knight-navy mb-3">A proven methodology to <em>raise your score</em></h2>
          <p className="text-knight-navy/70 leading-relaxed">Four pillars guide every dispute we file, every consultation we book, and every result we deliver.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {pillars.map((p, i) => (
            <div key={i} className="card-intense relative bg-gradient-to-br from-white to-slate-50 rounded-2xl p-6 border border-slate-200 overflow-hidden">
              <div className="absolute -top-3 -right-3 font-display text-7xl font-extrabold text-slate-100">0{i + 1}</div>
              <div className="relative">
                <div className="w-12 h-12 rounded-xl bg-knight-navy text-knight-gold flex items-center justify-center mb-4">
                  <p.icon size={22} />
                </div>
                <h3 className="font-display font-bold text-lg text-knight-navy mb-2">{p.title}</h3>
                <p className="text-sm text-knight-navy/70 leading-relaxed">{p.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Compact Step-by-Step section for the Home page (uses existing steps mock data)
export const StepByStepHome = () => {
  const visibleSteps = steps.slice(0, 6); // condensed view
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">OUR PROCESS</p>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-knight-navy mb-3">Step-by-step. <em>Stress-free.</em></h2>
          <p className="text-knight-navy/70 leading-relaxed">From free consultation to your target score &mdash; here&apos;s exactly how we&apos;ll work together.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {visibleSteps.map((step, i) => (
            <div key={i} className="card-intense relative bg-gradient-to-br from-white to-slate-50 rounded-2xl border border-slate-200 p-6 overflow-hidden">
              <div className="absolute -top-4 -right-3 font-display text-7xl font-extrabold text-slate-100">{String(i + 1).padStart(2, "0")}</div>
              <div className="relative">
                <div className="w-11 h-11 rounded-xl bg-knight-navy text-knight-gold flex items-center justify-center font-bold text-sm mb-3">{i + 1}</div>
                <p className="text-knight-navy/85 leading-relaxed text-sm">{step}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link to="/how-it-works">
            <Button variant="outline" className="border-knight-navy text-knight-navy hover:bg-knight-navy hover:text-white btn-lift">
              See the full 10-step process <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

// CTA banner
export const CTABanner = ({ title = "Are you ready to fix your credit?", subtitle, compact = false }) => (
  <section className={`${compact ? "py-12" : "py-16"} relative overflow-hidden knight-gradient text-white`}>
    <div className="absolute inset-0 grain-overlay" />
    <div className="absolute top-0 right-0 w-96 h-96 bg-knight-gold/10 rounded-full blur-3xl -mr-32 -mt-32" />
    <div className="relative max-w-7xl mx-auto px-4 text-center">
      <h3 className="font-display text-2xl md:text-3xl lg:text-5xl font-bold mb-3 text-center whitespace-nowrap">{title}</h3>
      {subtitle && <p className="text-white/80 mb-6 max-w-3xl mx-auto text-center">{subtitle}</p>}
      <a href="https://zcal.co/knightmediagroup/kcc1" target="_blank" rel="noopener noreferrer">
        <Button size="lg" className="bg-knight-gold hover:bg-knight-gold/90 text-knight-navy font-bold btn-lift mt-3">
          <Calendar size={18} className="mr-2" /> Schedule A Consultation
        </Button>
      </a>
    </div>
  </section>
);

// Testimonials continuous marquee scroll
export const TestimonialsPreview = () => {
  const loop = [...testimonials, ...testimonials];
  return (
    <section className="py-20 subtle-gradient overflow-hidden">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">WHAT OUR CLIENTS SAY</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-knight-navy text-center">Real stories from clients who achieved financial success with us</h2>
        </div>
      </div>

      <div className="relative">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-32 z-10 bg-gradient-to-r from-[#f4f6fc] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-32 z-10 bg-gradient-to-l from-[#f4f6fc] to-transparent" />

        <div className="marquee-track flex gap-6 w-max py-4 px-4">
          {loop.map((t, i) => {
            const gain = t.scoreAfter - t.scoreBefore;
            return (
              <div key={i} className="card-intense w-[380px] flex-shrink-0 bg-white rounded-3xl border border-slate-200 overflow-hidden relative">
                {/* Header strip with avatar + name + gain */}
                <div className="bg-gradient-to-r from-knight-navy to-knight-navy-light p-5 flex items-center gap-4 relative overflow-hidden">
                  <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-knight-gold/15 rounded-full blur-2xl" />
                  <div className="w-14 h-14 rounded-2xl bg-knight-gold flex items-center justify-center font-display font-bold text-2xl text-knight-navy shadow-lg flex-shrink-0">
                    {t.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0 relative">
                    <p className="font-bold text-white text-lg truncate">{t.name}</p>
                    <p className="text-xs text-white/65">{t.location} &middot; Verified client</p>
                  </div>
                  <div className="relative text-right flex-shrink-0">
                    <p className="text-[10px] tracking-widest font-bold text-knight-gold">GAIN</p>
                    <p className="font-display font-bold text-2xl text-white leading-none">+{gain}</p>
                  </div>
                </div>

                {/* Body */}
                <div className="p-6">
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: t.rating }).map((_, k) => <Star key={k} size={16} fill="#f4b400" stroke="#f4b400" />)}
                  </div>
                  <p className="text-knight-navy/85 leading-relaxed text-[15px] mb-5 italic min-h-[90px]">&ldquo;{t.quote}&rdquo;</p>

                  {/* Score bar visualization */}
                  <div className="bg-slate-50 rounded-xl p-3.5">
                    <div className="flex items-center justify-between mb-2 text-[11px] font-bold tracking-wider">
                      <span className="text-red-500">BEFORE {t.scoreBefore}</span>
                      <span className="text-emerald-600">AFTER {t.scoreAfter}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-200 overflow-hidden relative">
                      <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-red-400 via-amber-400 to-emerald-500 rounded-full" style={{ width: `${Math.min(100, ((t.scoreAfter - 300) / 550) * 100)}%` }} />
                    </div>
                    <div className="flex justify-between text-[9px] text-knight-navy/40 mt-1.5 font-mono">
                      <span>300</span><span>580</span><span>670</span><span>740</span><span>850</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const FAQ = () => (
  <section className="py-20">
    <div className="max-w-4xl mx-auto px-4">
      <div className="text-center mb-12">
        <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">FAQ</p>
        <h2 className="font-display text-4xl lg:text-5xl font-bold text-knight-navy">Frequently Asked <em>Questions</em></h2>
      </div>
      <Accordion type="single" collapsible className="space-y-3">
        {faqs.map((f, i) => (
          <AccordionItem key={i} value={`item-${i}`} className="border border-slate-200 rounded-xl px-5 data-[state=open]:border-knight-navy data-[state=open]:bg-slate-50/50">
            <AccordionTrigger className="text-left font-semibold text-knight-navy hover:no-underline">{f.q}</AccordionTrigger>
            <AccordionContent className="text-knight-navy/75 leading-relaxed">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);
